// Baduk (Go) analysis tool — pure game module
// Supports collaborative Go analysis with move tree, SGF import, and KataGo integration.

// === Constants ===

export const EMPTY = 0 as const;
export const BLACK = 1 as const;
export const WHITE = 2 as const;

const STAR_POINTS_BY_SIZE: Record<number, [number, number][]> = {
  9:  [[2, 2], [2, 6], [4, 4], [6, 2], [6, 6]],
  13: [[3, 3], [3, 9], [6, 6], [9, 3], [9, 9]],
  19: [[3, 3], [3, 9], [3, 15], [9, 3], [9, 9], [9, 15], [15, 3], [15, 9], [15, 15]],
};

const HANDICAP_POSITIONS_BY_SIZE: Record<number, Record<number, [number, number][]>> = {
  9: {
    2: [[2, 6], [6, 2]],
    3: [[2, 6], [6, 2], [6, 6]],
    4: [[2, 2], [2, 6], [6, 2], [6, 6]],
    5: [[2, 2], [2, 6], [6, 2], [6, 6], [4, 4]],
    6: [[2, 2], [2, 4], [2, 6], [6, 2], [6, 4], [6, 6]],
    7: [[2, 2], [2, 4], [2, 6], [6, 2], [6, 4], [6, 6], [4, 4]],
    8: [[2, 2], [2, 4], [2, 6], [4, 2], [4, 6], [6, 2], [6, 4], [6, 6]],
    9: [[2, 2], [2, 4], [2, 6], [4, 2], [4, 4], [4, 6], [6, 2], [6, 4], [6, 6]],
  },
  13: {
    2: [[3, 9], [9, 3]],
    3: [[3, 9], [9, 3], [9, 9]],
    4: [[3, 3], [3, 9], [9, 3], [9, 9]],
    5: [[3, 3], [3, 9], [9, 3], [9, 9], [6, 6]],
    6: [[3, 3], [3, 9], [9, 3], [9, 9], [3, 6], [9, 6]],
    7: [[3, 3], [3, 9], [9, 3], [9, 9], [3, 6], [9, 6], [6, 6]],
    8: [[3, 3], [3, 9], [9, 3], [9, 9], [3, 6], [9, 6], [6, 3], [6, 9]],
    9: [[3, 3], [3, 6], [3, 9], [6, 3], [6, 6], [6, 9], [9, 3], [9, 6], [9, 9]],
  },
  19: {
    2: [[3, 15], [15, 3]],
    3: [[3, 15], [15, 3], [15, 15]],
    4: [[3, 3], [3, 15], [15, 3], [15, 15]],
    5: [[3, 3], [3, 15], [15, 3], [15, 15], [9, 9]],
    6: [[3, 3], [3, 15], [15, 3], [15, 15], [3, 9], [15, 9]],
    7: [[3, 3], [3, 15], [15, 3], [15, 15], [3, 9], [15, 9], [9, 9]],
    8: [[3, 3], [3, 15], [15, 3], [15, 15], [3, 9], [15, 9], [9, 3], [9, 15]],
    9: [[3, 3], [3, 15], [15, 3], [15, 15], [3, 9], [15, 9], [9, 3], [9, 15], [9, 9]],
  },
};

// === Types ===

export type StoneColor = 0 | 1 | 2;
export type BoardState = StoneColor[][];
export interface Point { x: number; y: number; }
export type AnnotationType = 'triangle' | 'square' | 'circle' | 'x' | 'label';
export interface Annotation { type: AnnotationType; point: Point; label?: string; }

export interface BadukNode {
  id: string;
  move: Point | null;
  isPass: boolean;
  color: 'black' | 'white' | null;
  comment: string;
  annotations: Annotation[];
  childrenIds: string[];
  hasVariations: boolean;
  parentId: string | null;
  moveNumber: number;
}

export interface GameMetadata {
  blackPlayer: string;
  whitePlayer: string;
  komi: number;
  handicap: number;
  date: string;
  result: string | null;
  event?: string;
}

export interface BadukConfig {
  komi: number;
  handicap: number;
  boardSize?: number;
}

export const DEFAULT_CONFIG: BadukConfig = { komi: 6.5, handicap: 0, boardSize: 19 };

// State is what the relay broadcasts — matches BadukAnalysisState from the frontend types
export interface BadukState {
  type: 'baduk-analysis';
  started: boolean;
  phase: 'analyzing' | 'scoring' | 'finished';
  board: BoardState;
  consecutivePasses: number;
  moveTree: {
    rootId: 'root';
    currentNodeId: string;
    nodes: Record<string, BadukNode>;
    nodeCount: number;
    pathToCurrentNode: string[];
  };
  currentNode: {
    id: string;
    move: Point | null;
    isPass: boolean;
    color: 'black' | 'white' | null;
    comment: string;
    annotations: Annotation[];
    moveNumber: number;
    hasChildren: boolean;
    hasVariations: boolean;
    childrenIds: string[];
    parentId: string | null;
  };
  metadata: GameMetadata;
  currentTurn: 'black' | 'white';
  captures: { black: number; white: number };
  koPoint: Point | null;
  players: { playerId: string; playerName: string; isConnected: boolean }[];
  deadStones: string[];
  aiOpponent: { enabled: boolean; color: 'black' | 'white'; skillLevel: string; isThinking: boolean };
  config: { boardSize: number; starPoints: [number, number][] };
}

export type BadukAction =
  | { type: 'placeStone'; playerId: string; x: number; y: number }
  | { type: 'pass'; playerId: string }
  | { type: 'navigate'; direction: 'back' | 'forward' | 'to-node' | 'to-start' | 'to-end'; nodeId?: string }
  | { type: 'setComment'; comment: string }
  | { type: 'addAnnotation'; annotationType: AnnotationType; x: number; y: number; label?: string }
  | { type: 'removeAnnotation'; x: number; y: number }
  | { type: 'deleteVariation'; nodeId: string }
  | { type: 'toggleDeadStone'; x: number; y: number }
  | { type: 'acceptScore' }
  | { type: 'resumeGame' }
  | { type: 'reset'; keepMetadata?: boolean }
  | { type: 'loadSGF'; data: SGFData }
  | { type: 'configureAI'; settings: Partial<BadukState['aiOpponent']> }
  | { type: 'setAIThinking'; isThinking: boolean };

export interface SGFData {
  moves: { color: 'B' | 'W'; x?: number; y?: number; pass?: boolean; comment?: string }[];
  blackPlayer: string;
  whitePlayer: string;
  komi: number;
  handicap: number;
  date: string;
  result: string;
  event: string;
  rootComment: string;
  setupBlack: Point[];
  setupWhite: Point[];
}

// === Go Rules (pure functions) ===

function cloneBoard(board: BoardState): BoardState {
  return board.map(row => [...row]);
}

function createEmptyBoard(boardSize: number = 19): BoardState {
  return Array(boardSize).fill(null).map(() => Array(boardSize).fill(EMPTY));
}

function setupHandicap(board: BoardState, handicap: number, boardSize: number = 19): BoardState {
  if (handicap < 2 || handicap > 9) return board;
  const positions = HANDICAP_POSITIONS_BY_SIZE[boardSize] ?? HANDICAP_POSITIONS_BY_SIZE[19];
  const b = cloneBoard(board);
  for (const [x, y] of (positions[handicap] ?? [])) {
    b[y][x] = BLACK;
  }
  return b;
}

function adjacents(x: number, y: number, boardSize: number): Point[] {
  const pts: Point[] = [];
  if (x > 0) pts.push({ x: x - 1, y });
  if (x < boardSize - 1) pts.push({ x: x + 1, y });
  if (y > 0) pts.push({ x, y: y - 1 });
  if (y < boardSize - 1) pts.push({ x, y: y + 1 });
  return pts;
}

function getGroup(board: BoardState, x: number, y: number): Point[] {
  const color = board[y][x];
  if (color === EMPTY) return [];
  const bs = board.length;
  const group: Point[] = [];
  const visited = new Set<string>();
  const stack: Point[] = [{ x, y }];
  while (stack.length) {
    const p = stack.pop()!;
    const key = `${p.x},${p.y}`;
    if (visited.has(key)) continue;
    if (p.x < 0 || p.x >= bs || p.y < 0 || p.y >= bs) continue;
    if (board[p.y][p.x] !== color) continue;
    visited.add(key);
    group.push(p);
    stack.push({ x: p.x - 1, y: p.y }, { x: p.x + 1, y: p.y }, { x: p.x, y: p.y - 1 }, { x: p.x, y: p.y + 1 });
  }
  return group;
}

function countLiberties(board: BoardState, group: Point[]): number {
  const bs = board.length;
  const libs = new Set<string>();
  for (const p of group) {
    for (const a of adjacents(p.x, p.y, bs)) {
      if (board[a.y][a.x] === EMPTY) libs.add(`${a.x},${a.y}`);
    }
  }
  return libs.size;
}

function findCaptures(board: BoardState, x: number, y: number, color: StoneColor): Point[] {
  const opp = color === BLACK ? WHITE : BLACK;
  const captured: Point[] = [];
  const checked = new Set<string>();
  for (const a of adjacents(x, y, board.length)) {
    if (board[a.y][a.x] !== opp) continue;
    const key = `${a.x},${a.y}`;
    if (checked.has(key)) continue;
    const group = getGroup(board, a.x, a.y);
    group.forEach(p => checked.add(`${p.x},${p.y}`));
    if (countLiberties(board, group) === 0) captured.push(...group);
  }
  return captured;
}

function executeMove(board: BoardState, x: number, y: number, color: StoneColor): { board: BoardState; captures: Point[]; koPoint: Point | null } {
  const b = cloneBoard(board);
  b[y][x] = color;
  const caps = findCaptures(b, x, y, color);
  for (const c of caps) b[c.y][c.x] = EMPTY;
  let koPoint: Point | null = null;
  if (caps.length === 1) {
    const group = getGroup(b, x, y);
    const libs: Set<string> = new Set();
    for (const p of group) {
      for (const a of adjacents(p.x, p.y, b.length)) {
        if (b[a.y][a.x] === EMPTY) libs.add(`${a.x},${a.y}`);
      }
    }
    if (group.length === 1 && libs.size === 1) koPoint = caps[0];
  }
  return { board: b, captures: caps, koPoint };
}

function isValidMove(board: BoardState, x: number, y: number, color: StoneColor, koPoint: Point | null): { valid: boolean; reason: string } {
  const bs = board.length;
  if (x < 0 || x >= bs || y < 0 || y >= bs) return { valid: false, reason: 'Out of bounds' };
  if (board[y][x] !== EMPTY) return { valid: false, reason: 'Intersection is occupied' };
  if (koPoint && koPoint.x === x && koPoint.y === y) return { valid: false, reason: 'Ko rule violation' };
  const test = cloneBoard(board);
  test[y][x] = color;
  if (findCaptures(test, x, y, color).length > 0) return { valid: true, reason: '' };
  if (countLiberties(test, getGroup(test, x, y)) === 0) return { valid: false, reason: 'Suicide move' };
  return { valid: true, reason: '' };
}

function calculateScore(board: BoardState, komi: number, captures: { black: number; white: number }, deadStones: Set<string>) {
  const bs = board.length;
  const visited = new Set<string>();
  let blackTerritory = 0;
  let whiteTerritory = 0;
  const effectiveBoard = board.map((row, y) => row.map((cell, x) => deadStones.has(`${x},${y}`) ? EMPTY : cell));
  for (let y = 0; y < bs; y++) {
    for (let x = 0; x < bs; x++) {
      if (effectiveBoard[y][x] !== EMPTY || visited.has(`${x},${y}`)) continue;
      const region: Point[] = [];
      let touchesBlack = false;
      let touchesWhite = false;
      const stack: Point[] = [{ x, y }];
      while (stack.length) {
        const p = stack.pop()!;
        const key = `${p.x},${p.y}`;
        if (visited.has(key)) continue;
        if (p.x < 0 || p.x >= bs || p.y < 0 || p.y >= bs) continue;
        const stone = effectiveBoard[p.y][p.x];
        if (stone === BLACK) { touchesBlack = true; continue; }
        if (stone === WHITE) { touchesWhite = true; continue; }
        visited.add(key);
        region.push(p);
        stack.push({ x: p.x - 1, y: p.y }, { x: p.x + 1, y: p.y }, { x: p.x, y: p.y - 1 }, { x: p.x, y: p.y + 1 });
      }
      if (touchesBlack && !touchesWhite) blackTerritory += region.length;
      else if (touchesWhite && !touchesBlack) whiteTerritory += region.length;
    }
  }
  let deadBlack = 0;
  let deadWhite = 0;
  let livingBlack = 0;
  let livingWhite = 0;
  for (let y = 0; y < bs; y++) {
    for (let x = 0; x < bs; x++) {
      const isDead = deadStones.has(`${x},${y}`);
      const stone = board[y][x];
      if (stone === BLACK) { isDead ? deadBlack++ : livingBlack++; }
      else if (stone === WHITE) { isDead ? deadWhite++ : livingWhite++; }
    }
  }
  const bScore = blackTerritory + deadWhite + livingBlack;
  const wScore = whiteTerritory + deadBlack + livingWhite + komi;
  return { black: bScore, white: wScore, winner: bScore > wScore ? 'black' : wScore > bScore ? 'white' : 'tie', margin: Math.abs(bScore - wScore) };
}

// === Tree helpers ===

let nodeCounter = 0;
function newNodeId(): string {
  return `n${++nodeCounter}_${Math.random().toString(36).slice(2, 7)}`;
}

function getPath(nodes: Record<string, BadukNode>, fromId: string): string[] {
  const path: string[] = [];
  let cur: string | null = fromId;
  while (cur) {
    path.unshift(cur);
    cur = nodes[cur]?.parentId ?? null;
  }
  return path;
}

// Reconstruct board at a node by replaying moves from root
function boardAtNode(nodes: Record<string, BadukNode>, nodeId: string, handicap: number, boardSize: number = 19): { board: BoardState; koPoint: Point | null; captures: { black: number; white: number } } {
  const path = getPath(nodes, nodeId);
  let board = createEmptyBoard(boardSize);
  if (handicap > 0) board = setupHandicap(board, handicap, boardSize);
  let koPoint: Point | null = null;
  const captures = { black: 0, white: 0 };
  for (let i = 1; i < path.length; i++) {
    const node = nodes[path[i]];
    if (node.isPass) {
      koPoint = null;
    } else if (node.move) {
      const color = node.color === 'black' ? BLACK : WHITE;
      const result = executeMove(board, node.move.x, node.move.y, color);
      board = result.board;
      koPoint = result.koPoint;
      if (node.color === 'black') captures.black += result.captures.length;
      else captures.white += result.captures.length;
    }
  }
  return { board, koPoint, captures };
}

function deriveCurrentNode(nodes: Record<string, BadukNode>, nodeId: string): BadukState['currentNode'] {
  const n = nodes[nodeId];
  return {
    id: n.id, move: n.move, isPass: n.isPass, color: n.color, comment: n.comment,
    annotations: n.annotations, moveNumber: n.moveNumber,
    hasChildren: n.childrenIds.length > 0, hasVariations: n.hasVariations,
    childrenIds: n.childrenIds, parentId: n.parentId,
  };
}

function withChild(nodes: Record<string, BadukNode>, parentId: string, childId: string): Record<string, BadukNode> {
  const parent = nodes[parentId];
  const newChildrenIds = [...parent.childrenIds, childId];
  return { ...nodes, [parentId]: { ...parent, childrenIds: newChildrenIds, hasVariations: newChildrenIds.length > 1 } };
}

function deleteSubtree(nodes: Record<string, BadukNode>, nodeId: string): Record<string, BadukNode> {
  const result = { ...nodes };
  const toDelete: string[] = [nodeId];
  while (toDelete.length) {
    const id = toDelete.pop()!;
    const node = result[id];
    if (node) toDelete.push(...node.childrenIds);
    delete result[id];
  }
  return result;
}

// === Module exports ===

export function createGame(
  config: BadukConfig,
  players: { id: string; name: string; isBot: boolean }[],
  _seed?: number,
): BadukState {
  const komi = config.komi;
  const handicap = config.handicap;
  const boardSize = config.boardSize ?? 19;
  const starPoints = STAR_POINTS_BY_SIZE[boardSize] ?? STAR_POINTS_BY_SIZE[19];
  let board = createEmptyBoard(boardSize);
  if (handicap > 0) board = setupHandicap(board, handicap, boardSize);

  const root: BadukNode = {
    id: 'root', move: null, isPass: false, color: null,
    comment: '', annotations: [], childrenIds: [], hasVariations: false, parentId: null, moveNumber: 0,
  };

  return {
    type: 'baduk-analysis',
    started: true,
    phase: 'analyzing',
    board,
    consecutivePasses: 0,
    moveTree: {
      rootId: 'root',
      currentNodeId: 'root',
      nodes: { root },
      nodeCount: 1,
      pathToCurrentNode: ['root'],
    },
    currentNode: deriveCurrentNode({ root }, 'root'),
    metadata: {
      blackPlayer: players[0]?.name ?? 'Black',
      whitePlayer: players[1]?.name ?? 'White',
      komi,
      handicap,
      date: new Date().toISOString().split('T')[0],
      result: null,
    },
    currentTurn: handicap > 0 ? 'white' : 'black',
    captures: { black: 0, white: 0 },
    koPoint: null,
    players: players.map(p => ({ playerId: p.id, playerName: p.name, isConnected: true })),
    deadStones: [],
    aiOpponent: { enabled: false, color: 'white', skillLevel: '5k', isThinking: false },
    config: { boardSize, starPoints },
  };
}

export function applyAction(state: BadukState, action: BadukAction): BadukState {
  const { nodes, currentNodeId } = state.moveTree;

  switch (action.type) {
    case 'placeStone': {
      if (state.phase !== 'analyzing') return state;
      const color = state.currentTurn === 'black' ? BLACK : WHITE;
      const validation = isValidMove(state.board, action.x, action.y, color, state.koPoint);
      if (!validation.valid) return state;
      const { board, captures, koPoint } = executeMove(state.board, action.x, action.y, color);

      // Check if this move already exists as a child
      const currentNode = nodes[currentNodeId];
      let childId = currentNode.childrenIds.find(id => {
        const n = nodes[id];
        return n.move?.x === action.x && n.move?.y === action.y && n.color === state.currentTurn;
      });
      let newNodes = { ...nodes };
      let newNodeCount = state.moveTree.nodeCount;

      if (!childId) {
        const id = newNodeId();
        const moveNumber = currentNode.moveNumber + 1;
        newNodes[id] = { id, move: { x: action.x, y: action.y }, isPass: false, color: state.currentTurn, comment: '', annotations: [], childrenIds: [], hasVariations: false, parentId: currentNodeId, moveNumber };
        newNodes = withChild(newNodes, currentNodeId, id);
        childId = id;
        newNodeCount++;
      }

      const newCaptures = { black: state.captures.black + (state.currentTurn === 'black' ? captures.length : 0), white: state.captures.white + (state.currentTurn === 'white' ? captures.length : 0) };
      const newPath = getPath(newNodes, childId);

      return {
        ...state,
        board,
        koPoint,
        consecutivePasses: 0,
        currentTurn: state.currentTurn === 'black' ? 'white' : 'black',
        captures: newCaptures,
        moveTree: { ...state.moveTree, currentNodeId: childId, nodes: newNodes, nodeCount: newNodeCount, pathToCurrentNode: newPath },
        currentNode: deriveCurrentNode(newNodes, childId),
      };
    }

    case 'pass': {
      if (state.phase !== 'analyzing') return state;
      const currentNode = nodes[currentNodeId];
      const id = newNodeId();
      const moveNumber = currentNode.moveNumber + 1;
      const newNode: BadukNode = { id, move: null, isPass: true, color: state.currentTurn, comment: '', annotations: [], childrenIds: [], hasVariations: false, parentId: currentNodeId, moveNumber };
      const newNodes = withChild({ ...nodes, [id]: newNode }, currentNodeId, id);
      const newConsecutive = state.consecutivePasses + 1;
      const newPath = getPath(newNodes, id);
      const entering = newConsecutive >= 2;

      return {
        ...state,
        koPoint: null,
        consecutivePasses: entering ? 0 : newConsecutive,
        phase: entering ? 'scoring' : 'analyzing',
        currentTurn: state.currentTurn === 'black' ? 'white' : 'black',
        moveTree: { ...state.moveTree, currentNodeId: id, nodes: newNodes, nodeCount: state.moveTree.nodeCount + 1, pathToCurrentNode: newPath },
        currentNode: deriveCurrentNode(newNodes, id),
        deadStones: entering ? [] : state.deadStones,
      };
    }

    case 'navigate': {
      const currentNode = nodes[currentNodeId];
      let targetId: string | null = null;

      if (action.direction === 'back') {
        targetId = currentNode.parentId;
      } else if (action.direction === 'forward') {
        targetId = currentNode.childrenIds[0] ?? null;
      } else if (action.direction === 'to-node') {
        targetId = action.nodeId && nodes[action.nodeId] ? action.nodeId : null;
      } else if (action.direction === 'to-start') {
        targetId = 'root';
      } else if (action.direction === 'to-end') {
        let n = currentNode;
        while (n.childrenIds.length > 0) n = nodes[n.childrenIds[0]];
        targetId = n.id;
      }

      if (!targetId) return state;

      const { board, koPoint, captures } = boardAtNode(nodes, targetId, state.metadata.handicap, state.config.boardSize ?? 19);
      const targetNode = nodes[targetId];
      const newTurn = targetId === 'root'
        ? (state.metadata.handicap > 0 ? 'white' : 'black')
        : (targetNode.color === 'black' ? 'white' : 'black');

      return {
        ...state,
        board,
        koPoint,
        captures,
        currentTurn: newTurn as 'black' | 'white',
        consecutivePasses: 0,
        moveTree: { ...state.moveTree, currentNodeId: targetId, pathToCurrentNode: getPath(nodes, targetId) },
        currentNode: deriveCurrentNode(nodes, targetId),
      };
    }

    case 'setComment': {
      const node = nodes[currentNodeId];
      const newNodes = { ...nodes, [currentNodeId]: { ...node, comment: action.comment } };
      return { ...state, moveTree: { ...state.moveTree, nodes: newNodes }, currentNode: deriveCurrentNode(newNodes, currentNodeId) };
    }

    case 'addAnnotation': {
      const node = nodes[currentNodeId];
      const filtered = node.annotations.filter(a => !(a.point.x === action.x && a.point.y === action.y));
      const newAnn: Annotation = { type: action.annotationType, point: { x: action.x, y: action.y }, ...(action.label ? { label: action.label } : {}) };
      const newNodes = { ...nodes, [currentNodeId]: { ...node, annotations: [...filtered, newAnn] } };
      return { ...state, moveTree: { ...state.moveTree, nodes: newNodes }, currentNode: deriveCurrentNode(newNodes, currentNodeId) };
    }

    case 'removeAnnotation': {
      const node = nodes[currentNodeId];
      const newNodes = { ...nodes, [currentNodeId]: { ...node, annotations: node.annotations.filter(a => !(a.point.x === action.x && a.point.y === action.y)) } };
      return { ...state, moveTree: { ...state.moveTree, nodes: newNodes }, currentNode: deriveCurrentNode(newNodes, currentNodeId) };
    }

    case 'deleteVariation': {
      if (action.nodeId === 'root') return state;
      const node = nodes[action.nodeId];
      if (!node || !node.parentId) return state;
      const parent = nodes[node.parentId];
      // If we're in the subtree being deleted, navigate to parent first
      const path = getPath(nodes, currentNodeId);
      const deletingCurrent = path.includes(action.nodeId);
      const newParent = { ...parent, childrenIds: parent.childrenIds.filter(id => id !== action.nodeId) };
      let newNodes = deleteSubtree({ ...nodes, [node.parentId]: newParent }, action.nodeId);
      const newCurrent = deletingCurrent ? node.parentId : currentNodeId;
      if (deletingCurrent) {
        const { board, koPoint, captures } = boardAtNode(newNodes, newCurrent, state.metadata.handicap, state.config.boardSize ?? 19);
        const nc = newNodes[newCurrent];
        const newTurn = newCurrent === 'root' ? (state.metadata.handicap > 0 ? 'white' : 'black') : (nc.color === 'black' ? 'white' : 'black');
        return { ...state, board, koPoint, captures, currentTurn: newTurn as 'black' | 'white', moveTree: { ...state.moveTree, currentNodeId: newCurrent, nodes: newNodes, nodeCount: Object.keys(newNodes).length, pathToCurrentNode: getPath(newNodes, newCurrent) }, currentNode: deriveCurrentNode(newNodes, newCurrent) };
      }
      return { ...state, moveTree: { ...state.moveTree, nodes: newNodes, nodeCount: Object.keys(newNodes).length } };
    }

    case 'toggleDeadStone': {
      if (state.phase !== 'scoring') return state;
      const key = `${action.x},${action.y}`;
      const deadStones = state.deadStones.includes(key)
        ? state.deadStones.filter(k => k !== key)
        : [...state.deadStones, key];
      return { ...state, deadStones };
    }

    case 'acceptScore': {
      if (state.phase !== 'scoring') return state;
      const score = calculateScore(state.board, state.metadata.komi, state.captures, new Set(state.deadStones));
      const result = score.winner === 'tie' ? 'Tie' : `${score.winner === 'black' ? 'B' : 'W'}+${score.margin.toFixed(1)}`;
      return { ...state, phase: 'finished', metadata: { ...state.metadata, result } };
    }

    case 'resumeGame': {
      if (state.phase !== 'scoring') return state;
      return { ...state, phase: 'analyzing', deadStones: [], consecutivePasses: 0 };
    }

    case 'reset': {
      const { komi, handicap } = state.metadata;
      const boardSize = state.config.boardSize ?? 19;
      let board = createEmptyBoard(boardSize);
      if (handicap > 0) board = setupHandicap(board, handicap, boardSize);
      const root: BadukNode = { id: 'root', move: null, isPass: false, color: null, comment: '', annotations: [], childrenIds: [], hasVariations: false, parentId: null, moveNumber: 0 };
      const newMetadata = action.keepMetadata ? state.metadata : { ...state.metadata, result: null };
      return {
        ...state,
        board,
        phase: 'analyzing',
        consecutivePasses: 0,
        koPoint: null,
        captures: { black: 0, white: 0 },
        currentTurn: handicap > 0 ? 'white' : 'black',
        deadStones: [],
        metadata: newMetadata,
        moveTree: { rootId: 'root', currentNodeId: 'root', nodes: { root }, nodeCount: 1, pathToCurrentNode: ['root'] },
        currentNode: deriveCurrentNode({ root }, 'root'),
      };
    }

    case 'loadSGF': {
      return loadSGFIntoState(state, action.data);
    }

    case 'configureAI': {
      return { ...state, aiOpponent: { ...state.aiOpponent, ...action.settings } };
    }

    case 'setAIThinking': {
      return { ...state, aiOpponent: { ...state.aiOpponent, isThinking: action.isThinking } };
    }

    default:
      return state;
  }
}

function loadSGFIntoState(state: BadukState, data: SGFData): BadukState {
  const handicap = data.handicap || 0;
  const boardSize = state.config.boardSize ?? 19;
  let board = createEmptyBoard(boardSize);
  for (const p of data.setupBlack) board[p.y][p.x] = BLACK;
  for (const p of data.setupWhite) board[p.y][p.x] = WHITE;
  if (handicap > 0 && data.setupBlack.length === 0) board = setupHandicap(board, handicap, boardSize);

  const root: BadukNode = { id: 'root', move: null, isPass: false, color: null, comment: data.rootComment || '', annotations: [], childrenIds: [], hasVariations: false, parentId: null, moveNumber: 0 };
  let nodes: Record<string, BadukNode> = { root };
  let parentId = 'root';
  let parentMoveNumber = 0;
  let currentTurn: 'black' | 'white' = handicap > 0 ? 'white' : 'black';

  for (const move of data.moves) {
    const id = newNodeId();
    const color = move.color === 'B' ? 'black' : 'white';
    const node: BadukNode = {
      id, move: move.pass ? null : { x: move.x!, y: move.y! },
      isPass: move.pass ?? false, color,
      comment: move.comment || '', annotations: [],
      childrenIds: [], hasVariations: false, parentId, moveNumber: parentMoveNumber + 1,
    };
    nodes = withChild({ ...nodes, [id]: node }, parentId, id);
    parentId = id;
    parentMoveNumber++;
    currentTurn = color === 'black' ? 'white' : 'black';
  }

  // Reconstruct board at last node
  const lastId = parentId;
  const { board: finalBoard, koPoint, captures } = boardAtNode(nodes, lastId, handicap, boardSize);
  const pathToCurrentNode = getPath(nodes, lastId);

  return {
    ...state,
    board: finalBoard,
    koPoint,
    captures,
    consecutivePasses: 0,
    phase: 'analyzing',
    currentTurn,
    deadStones: [],
    metadata: {
      ...state.metadata,
      blackPlayer: data.blackPlayer || state.metadata.blackPlayer,
      whitePlayer: data.whitePlayer || state.metadata.whitePlayer,
      komi: data.komi || state.metadata.komi,
      handicap,
      date: data.date || state.metadata.date,
      result: data.result || null,
      event: data.event || state.metadata.event,
    },
    moveTree: { rootId: 'root', currentNodeId: lastId, nodes, nodeCount: Object.keys(nodes).length, pathToCurrentNode },
    currentNode: deriveCurrentNode(nodes, lastId),
  };
}

export function getPlayerView(state: BadukState, _playerId: string | null): BadukState {
  return state;
}

export function describe(config: BadukConfig): string {
  const boardSize = config.boardSize ?? 19;
  return [
    `Baduk (Go) — Analysis Tool. ${boardSize}×${boardSize} board. Komi: ${config.komi}. Handicap: ${config.handicap}.`,
    '',
    'Collaborative Go analysis workspace. Players take turns placing Black and White stones.',
    'A group of stones with no liberties (open adjacent intersections) is captured and removed.',
    'Ko rule: a move that recreates the previous board position is illegal.',
    'Two consecutive passes end the game and trigger final scoring.',
    'Score = territory (empty points surrounded by your color) + captures − opponent captures.',
    'White receives komi points to offset Black\'s first-move advantage.',
    '',
    'Also supports: SGF game import, branching move tree navigation, board annotations, and KataGo AI move analysis.',
  ].join('\n');
}

// === SGF Parser ===

export function parseSGFContent(content: string): SGFData {
  const result: SGFData = {
    moves: [], blackPlayer: '', whitePlayer: '', komi: 6.5, handicap: 0,
    date: '', result: '', event: '', rootComment: '', setupBlack: [], setupWhite: [],
  };
  content = content.replace(/\s+/g, ' ').trim();
  const rootMatch = content.match(/\(;([^;(]+)/);
  if (rootMatch) {
    const propRegex = /([A-Z]+)\[([^\]]*)\]/g;
    let m: RegExpExecArray | null;
    while ((m = propRegex.exec(rootMatch[1])) !== null) {
      const [, prop, value] = m;
      switch (prop) {
        case 'PB': result.blackPlayer = value; break;
        case 'PW': result.whitePlayer = value; break;
        case 'KM': result.komi = parseFloat(value) || 6.5; break;
        case 'HA': result.handicap = parseInt(value) || 0; break;
        case 'DT': result.date = value; break;
        case 'RE': result.result = value; break;
        case 'EV': result.event = value; break;
        case 'C': result.rootComment = value; break;
        case 'AB': { const p = sgfToPoint(value); if (p) result.setupBlack.push(p); break; }
        case 'AW': { const p = sgfToPoint(value); if (p) result.setupWhite.push(p); break; }
      }
    }
  }
  const moveRegex = /;([BW])\[([a-s]{0,2})\]/g;
  let m: RegExpExecArray | null;
  while ((m = moveRegex.exec(content)) !== null) {
    const [, color, coord] = m as unknown as [string, 'B' | 'W', string];
    if (coord === '' || coord === 'tt') {
      result.moves.push({ color, pass: true });
    } else {
      const p = sgfToPoint(coord);
      if (p) result.moves.push({ color, x: p.x, y: p.y });
    }
  }
  return result;
}

function sgfToPoint(coord: string): Point | null {
  if (coord.length !== 2) return null;
  const x = coord.charCodeAt(0) - 97;
  const y = coord.charCodeAt(1) - 97;
  if (x < 0 || x > 18 || y < 0 || y > 18) return null;
  return { x, y };
}

export function getValidActions(_state: BadukState, _playerId: string): string[] {
  return [];
}
